import { scaleTime } from '@visx/scale';
import { useTheme } from '@simplywallst/ui-core';
import Label from '../Label';
import BuyLimit from '../BuyLimit';
import { Tooltip } from './styled';
import { useDividendTranslation } from '../../../../hooks/useDividendTranslations';
import { useDateFormatter } from '@/hooks/useDateFormatter';

interface Props {
  dividendNextDate: number;
  dividendNextPayDate: number;
}

const dateFormat = 'MMM dd yyyy';

const now = Date.now();

const NextDividend = ({ dividendNextDate, dividendNextPayDate }: Props) => {
  const theme = useTheme();
  const xScaleMax = Math.max(dividendNextDate, dividendNextPayDate);
  const xScale = scaleTime({
    range: [0, 1],
    domain: [now, xScaleMax],
  });
  const dividendNextDateX = (xScale(dividendNextDate) as number) * 100;
  const t = useDividendTranslation();
  const { formatUTC, formatDistanceStrict } = useDateFormatter();

  return (
    <svg height="224" width="100%" shapeRendering="crispEdges">
      <Label
        x={`${(xScale(now) as number) * 100}%`}
        y="168"
        title={t('nextDividend.label.today')}
        date={formatUTC(new Date(now), dateFormat)}
      />
      <Label
        x={`${dividendNextDateX}%`}
        y="0"
        title={t('nextDividend.label.exDividendDate')}
        date={formatUTC(new Date(dividendNextDate), dateFormat)}
        textAnchor={dividendNextDateX > 50 ? 'end' : 'start'}
        tooltip={() => (
          <Tooltip>{t('nextDividend.tooltip.exDividendDate')}</Tooltip>
        )}
      />
      {dividendNextPayDate && (
        <Label
          x={`${(xScale(dividendNextPayDate) as number) * 100}%`}
          y="168"
          highlight={true}
          title={t('nextDividend.label.dividendPayDate')}
          date={formatUTC(new Date(dividendNextPayDate), dateFormat)}
          footer={t('nextDividend.label.distanceFromPayDate', {
            days: formatDistanceStrict(dividendNextPayDate, dividendNextDate, {
              unit: 'day',
            }),
          })}
          textAnchor="end"
        />
      )}

      <svg
        y={theme.x5spacePx + 3}
        height={theme.x15spacePx}
        width="100%"
        shapeRendering="crispEdges"
      >
        <rect
          x="0"
          y={theme.x3spacePx}
          height={theme.x5spacePx}
          width="100%"
          fill={theme.color.chart03}
        />
        <rect
          x="0"
          y={theme.x3spacePx}
          height={theme.x5spacePx}
          width={`${(xScale(dividendNextDate) as number) * 100}%`}
          fill={theme.color.chart01}
        />
        <rect
          x="0"
          y={theme.x8spacePx}
          height={theme.x7spacePx}
          width="2"
          fill={theme.color.chart01}
        />
        <BuyLimit
          title={t('nextDividend.label.buyLimit', {
            days: formatDistanceStrict(dividendNextDate, now, {
              unit: 'day',
            }),
          })}
        />
        <rect
          x={`${(xScale(dividendNextDate) as number) * 100}%`}
          y="0"
          height={theme.x3spacePx}
          width="2"
          fill={dividendNextPayDate ? theme.color.chart03 : theme.color.chart01}
          transform={`translate(${dividendNextPayDate ? 0 : -2}, 0)`}
        />
        {dividendNextPayDate && (
          <g transform="translate(-2, 0)">
            <rect
              x="100%"
              y={theme.x8spacePx}
              height={theme.x7spacePx}
              width="2"
              fill={theme.color.chart03}
            />
          </g>
        )}
      </svg>
    </svg>
  );
};

export default NextDividend;
